import { useMutation, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
} from "react";
import { useWalletsQuery } from "../hooks/useWalletsQuery";
import { walletUpdatesToSnake } from "../lib/walletSnake";
import { dataProviders } from "../services/data";
import { trpc } from "../trpc";
import type {
	ChecklistItemSnake,
	Comment,
	Transaction,
	Wallet,
} from "../types";
import { useAuth } from "./AuthContext";

interface WalletContextType {
	wallets: Wallet[];
	loading: boolean;
	error: string | null;
	isUpdating: boolean;
	addWallet: (wallet: Wallet) => Promise<Wallet>;
	updateWallet: (id: string, updates: Partial<Wallet>) => Promise<void>;
	deleteWallet: (id: string) => Promise<void>;
	getWallet: (id: string) => Wallet | undefined;
	addComment: (walletId: string, comment: Comment) => Promise<void>;
	updateChecklist: (
		walletId: string,
		checklistKey: keyof Wallet,
		updates: Partial<ChecklistItemSnake>,
	) => Promise<void>;
	addTransaction: (walletId: string, transaction: Transaction) => Promise<void>;
	deleteTransaction: (walletId: string, transactionId: string) => Promise<void>;
	refreshWallets: () => Promise<void>;
	applyProposal: (walletId: string) => Promise<void>;
	rejectProposal: (walletId: string) => Promise<void>;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

/** Minimal stub when WalletProvider is unavailable (avoids crash in error states). */
const STUB_CONTEXT: WalletContextType = {
	wallets: [],
	loading: false,
	error: null,
	isUpdating: false,
	addWallet: async () => {
		throw new Error("WalletProvider not available");
	},
	updateWallet: async () => {},
	deleteWallet: async () => {},
	getWallet: () => undefined,
	addComment: async () => {},
	updateChecklist: async () => {},
	addTransaction: async () => {},
	deleteTransaction: async () => {},
	refreshWallets: async () => {},
	applyProposal: async () => {},
	rejectProposal: async () => {},
};

export const useWallets = () => {
	const context = useContext(WalletContext);
	if (!context) {
		// In error/recovery states (e.g. after submit 500), provider tree may be disrupted.
		// Return stub so KYCForm can at least show error UI instead of crashing.
		if (import.meta.env.DEV) {
			console.warn("useWallets called outside WalletProvider; using stub");
		}
		return STUB_CONTEXT;
	}
	return context;
};

interface WalletProviderProps {
	children: ReactNode;
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
	const queryClient = useQueryClient();
	const { isAuthenticated, loading: authLoading, user } = useAuth();

	const {
		data: wallets = [],
		isLoading: loading,
		error: queryError,
		refetch,
	} = useWalletsQuery(user?.id, user?.role, !!isAuthenticated && !authLoading);

	const error = queryError
		? queryError instanceof Error
			? queryError.message
			: "Failed to load wallets"
		: null;

	const invalidateWallets = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["wallets"] });
	}, [queryClient]);

	/**
	 * Updates a specific wallet in the React Query cache without refetching all wallets.
	 * Returns true if the cache was updated, false otherwise (e.g. user not authenticated or cache empty).
	 */
	const updateWalletInCache = useCallback(
		(updatedWallet: Wallet): boolean => {
			if (!user?.id || !user?.role) {
				if (import.meta.env.DEV) {
					console.warn("Cannot update wallet in cache: user not authenticated");
				}
				return false;
			}
			const queryKey = ["wallets", user.id, user.role];
			let cacheUpdated = false;
			queryClient.setQueryData<Wallet[]>(queryKey, (oldWallets) => {
				if (!oldWallets) {
					if (import.meta.env.DEV) {
						console.warn("No wallets in cache to update");
					}
					return oldWallets;
				}
				cacheUpdated = true;
				return oldWallets.map((w) =>
					w.id === updatedWallet.id ? updatedWallet : w,
				);
			});
			return cacheUpdated;
		},
		[queryClient, user?.id, user?.role],
	);

	const handleWalletMutationSuccess = useCallback(
		(updatedWallet: Wallet, invalidateTransactions = false) => {
			try {
				const cacheUpdated = updateWalletInCache(updatedWallet);
				if (!cacheUpdated) {
					queryClient.invalidateQueries({ queryKey: ["wallets"] });
				}
			} catch (error) {
				console.error(
					"Failed to update wallet in cache, falling back to invalidation:",
					error,
				);
				queryClient.invalidateQueries({ queryKey: ["wallets"] });
			}
			if (invalidateTransactions) {
				queryClient.invalidateQueries({ queryKey: ["walletTransactions"] });
			}
		},
		[queryClient, updateWalletInCache],
	);

	/** Create a mutation that automatically invalidates the wallets cache on success. */
	const walletMutation = <T, R = unknown>(fn: (arg: T) => Promise<R>) =>
		// biome-ignore lint/correctness/useHookAtTopLevel: walletMutation is a local factory, each call is unconditional at the top level of WalletProvider
		useMutation({
			mutationFn: fn,
			onSuccess: () => queryClient.invalidateQueries({ queryKey: ["wallets"] }),
		});

	const addWalletMutation = trpc.wallet.create.useMutation({
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["wallets"] }),
	});

	const updateWalletMutation = trpc.wallet.update.useMutation({
		onSuccess: (updatedWallet) => {
			handleWalletMutationSuccess(updatedWallet as Wallet);
		},
	});

	const deleteWalletMutation = walletMutation((id: string) =>
		dataProviders.wallet.delete(id),
	);

	// Use targeted cache update for comment additions
	const addCommentMutation = useMutation({
		mutationFn: ({
			walletId,
			comment,
		}: {
			walletId: string;
			comment: Comment;
		}) => dataProviders.wallet.addComment(walletId, comment),
		onSuccess: (updatedWallet) => {
			handleWalletMutationSuccess(updatedWallet);
		},
	});

	// Use targeted cache update for checklist updates
	const updateChecklistMutation = useMutation({
		mutationFn: ({
			walletId,
			checklistKey,
			updates,
		}: {
			walletId: string;
			checklistKey: keyof Wallet;
			updates: Partial<ChecklistItemSnake>;
		}) => dataProviders.wallet.updateChecklist(walletId, checklistKey, updates),
		onSuccess: (updatedWallet) => {
			handleWalletMutationSuccess(updatedWallet);
		},
	});

	// Use targeted cache update for transaction additions
	const addTransactionMutation = useMutation({
		mutationFn: ({
			walletId,
			transaction,
		}: {
			walletId: string;
			transaction: Transaction;
		}) => dataProviders.wallet.addTransaction(walletId, transaction),
		onSuccess: (updatedWallet) => {
			handleWalletMutationSuccess(updatedWallet, true);
		},
	});

	// Use targeted cache update for transaction deletions
	const deleteTransactionMutation = useMutation({
		mutationFn: ({
			walletId,
			transactionId,
		}: {
			walletId: string;
			transactionId: string;
		}) => dataProviders.wallet.deleteTransaction(walletId, transactionId),
		onSuccess: (updatedWallet) => {
			handleWalletMutationSuccess(updatedWallet, true);
		},
	});

	const getWallet = useCallback(
		(id: string) => wallets.find((w) => w.id === id),
		[wallets],
	);

	const addWallet = useCallback(
		async (wallet: Wallet): Promise<Wallet> => {
			const created = await addWalletMutation.mutateAsync({
				wallet_name: wallet.wallet_name,
				company_name: wallet.company_name,
				chain: wallet.chain,
				asset: wallet.asset,
				custodial_type: wallet.custodial_type,
				products: wallet.products,
				main_contact_name: wallet.main_contact_name,
				main_contact_email: wallet.main_contact_email,
				main_contact_phone: wallet.main_contact_phone,
				main_contact_role: wallet.main_contact_role,
				priority_markets: wallet.priority_markets,
				country_of_incorporation: wallet.country_of_incorporation,
				mau: typeof wallet.mau === "number" ? wallet.mau : undefined,
				conducts_kyc: wallet.conducts_kyc,
				home_domain: wallet.home_domain,
				integration_status: wallet.integration_status,
				onboarding_status: wallet.onboarding_status,
				is_existing_partner: wallet.is_existing_partner,
				is_whitelisted: wallet.is_whitelisted,
				whitelist_environments: wallet.whitelist_environments,
			});
			// Optimistically inject the new wallet into the cache so getWallet(id) finds it
			// immediately on redirect — avoids 404 from read-after-write race.
			const createdWallet = created as Wallet;
			queryClient.setQueryData<{ data: Wallet[]; pagination: unknown }>(
				["wallets"],
				(old) =>
					old ? { ...old, data: [createdWallet, ...old.data] } : undefined,
			);
			return createdWallet;
		},
		[addWalletMutation],
	);

	const updateWallet = useCallback(
		async (id: string, updates: Partial<Wallet>) => {
			const snakeUpdates = walletUpdatesToSnake(
				updates as unknown as Record<string, unknown>,
			);
			await updateWalletMutation.mutateAsync({ id, updates: snakeUpdates });
		},
		[updateWalletMutation],
	);

	const deleteWallet = useCallback(
		async (id: string) => {
			await deleteWalletMutation.mutateAsync(id);
		},
		[deleteWalletMutation],
	);

	const addComment = useCallback(
		async (walletId: string, comment: Comment) => {
			await addCommentMutation.mutateAsync({ walletId, comment });
		},
		[addCommentMutation],
	);

	const updateChecklist = useCallback(
		async (
			walletId: string,
			checklistKey: keyof Wallet,
			updates: Partial<ChecklistItemSnake>,
		) => {
			await updateChecklistMutation.mutateAsync({
				walletId,
				checklistKey,
				updates,
			});
		},
		[updateChecklistMutation],
	);

	const addTransaction = useCallback(
		async (walletId: string, transaction: Transaction) => {
			await addTransactionMutation.mutateAsync({ walletId, transaction });
		},
		[addTransactionMutation],
	);

	const deleteTransaction = useCallback(
		async (walletId: string, transactionId: string) => {
			await deleteTransactionMutation.mutateAsync({
				walletId,
				transactionId,
			});
		},
		[deleteTransactionMutation],
	);

	const refreshWallets = useCallback(async () => {
		await refetch();
	}, [refetch]);

	const applyProposal = useCallback(
		async (walletId: string) => {
			if (!dataProviders.wallet.applyProposal) {
				throw new Error("Apply proposal not supported");
			}
			await dataProviders.wallet.applyProposal(walletId);
			invalidateWallets();
		},
		[invalidateWallets],
	);

	const rejectProposal = useCallback(
		async (walletId: string) => {
			if (!dataProviders.wallet.rejectProposal) {
				throw new Error("Reject proposal not supported");
			}
			await dataProviders.wallet.rejectProposal(walletId);
			invalidateWallets();
		},
		[invalidateWallets],
	);

	const isUpdating =
		updateWalletMutation.isPending || updateChecklistMutation.isPending;

	const contextValue = useMemo(
		() => ({
			wallets,
			loading,
			error,
			isUpdating,
			addWallet,
			updateWallet,
			deleteWallet,
			getWallet,
			addComment,
			updateChecklist,
			addTransaction,
			deleteTransaction,
			refreshWallets,
			applyProposal,
			rejectProposal,
		}),
		[
			wallets,
			loading,
			error,
			isUpdating,
			addWallet,
			updateWallet,
			deleteWallet,
			getWallet,
			addComment,
			updateChecklist,
			addTransaction,
			deleteTransaction,
			refreshWallets,
			applyProposal,
			rejectProposal,
		],
	);

	return (
		<WalletContext.Provider value={contextValue}>
			{children}
		</WalletContext.Provider>
	);
};
